import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, API_ENDPOINTS, ApiError } from '../config/api';
import { STUDIO_SCOPED_ROLES } from '../utils/roles';
import { studioIdOf } from '../utils/studioOf';

/**
 * Bulk range operations (create / update / delete a class across a date span)
 * walk hundreds–thousands of occurrences server-side and routinely run past the
 * instance's default 30s timeout. When the client aborts mid-write the backend
 * keeps going: the operator sees a failure, retries, and ends up with a
 * duplicated range. Give these calls room to actually finish so the dialog can
 * hold its loading state until the server responds.
 */
const BULK_RANGE_TIMEOUT_MS = 180_000;

/**
 * Creating a campaign SENDS it synchronously server-side — the request only
 * returns once every FCM chunk has gone out and the campaign's status/counts
 * are written. For a large audience that runs well past the default 30s, and a
 * client abort there leaves the UI showing "failed" for a campaign that the
 * backend goes on to mark SENT. Wait for the real result instead.
 */
const CAMPAIGN_SEND_TIMEOUT_MS = 180_000;

type UpdateUserPayload = Partial<{
  name: string;
  email?: string;
  phoneNumber: string;
  age?: number;
  city?: string;
  gender?: string;
  registrationSource?: string;
  isMarketable?: boolean;
}>;

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
    });

    this.setupInterceptors();
  }

  /** The location this account has chosen, if any. Never another account's. */
  private selectedStudioId(): string | null {
    try {
      if (!localStorage.getItem('admin_data')) return null; // signed out
      // The stable account key, written at login. NOT `admin.id`, which for a
      // studio login reports the location being viewed and therefore moves.
      let key = localStorage.getItem('admin_account_key');
      if (!key) {
        // A session that began before this key existed (restored from
        // storage, never passed through login()). Derive it the same way
        // login() does, or the location switcher is written but never sent.
        const a = JSON.parse(localStorage.getItem('admin_data') || '{}');
        key = a?.email || a?.id || null;
        if (!key) return null;
        localStorage.setItem('admin_account_key', String(key));
      }
      // ── "ALL LOCATIONS" IS SENT, NOT OMITTED ────────────────────────
      //
      // This used to return null for `__all__`, so the aggregate view sent no
      // header at all — indistinguishable on the wire from an ordinary
      // single-studio login that has no location to send. The server cannot
      // tell those apart, so both resolved to the token's own studio and
      // "All locations" silently meant "my default branch": a brand owner
      // with 9 classes at one branch and 2 at another saw the 9, with nothing
      // on screen saying the rest had been dropped.
      //
      // Sending the sentinel makes the two cases distinct. The server widens
      // reads to every location the caller's grants cover, and REFUSES writes
      // (a change has to land at one place) — see studio-scope.helper.ts.
      return localStorage.getItem(`selected_studio_id:${key}`) || null;
    } catch {
      return null;
    }
  }

  isStudio(): boolean {
    try {
      const adminData = localStorage.getItem('admin_data');
      if (adminData) {
        const admin = JSON.parse(adminData);
        return STUDIO_SCOPED_ROLES.includes(admin.role);
      }
      return false;
    } catch {
      return false;
    }
  }

  private isStudioUser(): boolean {
    try {
      const adminData = localStorage.getItem('admin_data');
      if (adminData) {
        const admin = JSON.parse(adminData);
        return admin.role === 'manager' || admin.role === 'instructor';
      }
      return false;
    } catch {
      return false;
    }
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // ── Which location this request is about ────────────────────────────
        // Only ever set by someone who holds more than one (see
        // LocationSwitcher). The server validates it against their grants and
        // swaps what `req.studio` resolves to, which is what lets every
        // existing studio-scoped endpoint follow the switch without being
        // touched. An id outside the grant is refused with 404, never honoured.
        // Read against the CURRENT account. A single global key meant one
        // login inherited another's selection and every request in the new
        // session came back "Studio not found" — see LocationSwitcher.
        const selectedStudio = this.selectedStudioId();
        // ── A CALL THAT NAMES ITS LOCATION WINS ─────────────────────────
        //
        // Some calls pin one location explicitly — the brand dashboard fans
        // out one request per branch (getStudioTransactionStatsFor,
        // getBookingsFor), and every row action targets the studio its row
        // belongs to (see atStudio). Those already carry the right header.
        //
        // Overwriting it here made every one of those fan-out requests ask
        // for `__all__`, so each branch row reported the BRAND total: two
        // locations showing identical revenue, and a Total row that summed
        // the same figure twice. The switcher is the DEFAULT, not an
        // override.
        const alreadyPinned = Boolean(
          (config.headers as any)?.['x-studio-id'],
        );
        if (selectedStudio && !alreadyPinned) {
          config.headers['x-studio-id'] = selectedStudio;
        }

        // FormData needs a multipart boundary — the instance default
        // `Content-Type: application/json` prevents multer from seeing files.
        if (config.data instanceof FormData) {
          config.headers.delete('Content-Type');
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          const errorMessage = (error.response?.data?.message || '').toLowerCase();
          // Only redirect to login for actual token errors (invalid/expired token, no token)
          // Don't redirect for permission errors (Studio not found, Admin access required, etc.)
          // A revoked location grant ends the session the same way: every
          // request the switcher sends for that location is now refused.
          const isTokenError =
            errorMessage.includes('token') ||
            errorMessage.includes('expired') ||
            errorMessage.includes('access to this location has been removed') ||
            !localStorage.getItem('admin_token');
          if (isTokenError) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_data');
            window.location.href = '/login';
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: any): ApiError {
    if (error.response) {
      return {
        message: error.response.data?.message || 'An error occurred',
        status: error.response.status,
        code: error.response.data?.code,
        // Carry the confirmable-warning flag through. Callers never see the
        // raw axios error — this interceptor flattens it — so anything not
        // copied here is lost to every caller.
        overridable: error.response.data?.overridable === true,
        // ...which is exactly how the instructor-removal picker went missing:
        // the refusal's counts were dropped here, and the pages then looked for
        // them at `err.response.data`, which this flattening had already
        // removed. Pass the body through whole rather than growing this list.
        data: error.response.data,
      };
    } else if (error.request) {
      return {
        message: 'Network error - please check your connection',
        status: 0,
      };
    } else {
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  // Generic HTTP methods
  /**
   * ── ACT ON ONE LOCATION, WHATEVER THE SWITCHER SAYS ─────────────────────
   *
   * While "All locations" is selected the interceptor sends the `__all__`
   * sentinel, which the server accepts for READS and refuses for writes — a
   * change has to land at one place.
   *
   * But an aggregated list shows rows from every branch, and editing the
   * Yelahanka class in that list is not ambiguous at all: the row says where
   * it lives. So a row action pins the request to the row's OWN studio and
   * the write resolves exactly where the operator is looking.
   *
   * Pass `row.studio._id`. Undefined falls back to the switcher, which is
   * correct for anything that is not acting on a specific row.
   */
  atStudio(studioId?: any): AxiosRequestConfig {
    // Routed through studioIdOf so a populated `{ _id, studioName }` never
    // becomes "[object Object]". Null / unknown → an empty config (the
    // switcher decides), which is also safe to spread.
    const id = studioIdOf(studioId);
    return id ? { headers: { 'x-studio-id': id } } : {};
  }

  /**
   * The studio this LOGIN belongs to — the token's own studio (`_id` in the
   * JWT payload), not whichever location is being viewed (`admin.studio`
   * follows the switcher). Falls back to the stored admin studio.
   */
  ownStudioId(): string | null {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        const part = token.split('.')[1];
        if (part) {
          const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
          const id = JSON.parse(json)?._id;
          if (id) return String(id);
        }
      }
    } catch {
      /* not a JWT we can read — fall through */
    }
    try {
      const a = JSON.parse(localStorage.getItem('admin_data') || '{}');
      return studioIdOf(a?.studio);
    } catch {
      return null;
    }
  }

  /**
   * For a write that is about the ACCOUNT rather than a row (uploads, member
   * edits, feature requests): under "All locations" the switcher's `__all__`
   * is refused for writes, so pin it to the account's own studio. Anywhere
   * else (super admin, a single location, a specific location chosen) this is
   * empty and the request behaves exactly as before.
   */
  atOwnStudio(): AxiosRequestConfig {
    if (!this.isStudio() || this.selectedStudioId() !== '__all__') return {};
    return this.atStudio(this.ownStudioId());
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete(url, config);
    return response.data;
  }

  // Authentication methods
  async login(credentials: { email: string; password: string }) {
    return this.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
  }

  async logout() {
    return this.post(API_ENDPOINTS.AUTH.LOGOUT);
  }

  async generateStudioSignupOtp(email: string) {
    return this.post('/api/v1/auth/generateStudioSignupOtp', { email });
  }

  async createStudio(data: { studioName: string; email: string; password: string; otp: string; logo?: string }) {
    return this.post('/api/v1/auth/studioSignup', data);
  }

  async refreshToken() {
    return this.post(API_ENDPOINTS.AUTH.REFRESH);
  }

  // ===== Studio password reset (mirrors the partner app flow) =====
  // Step 1: send a reset OTP to the studio's email.
  async generateResetPasswordOtp(email: string) {
    return this.post(API_ENDPOINTS.AUTH.GENERATE_RESET_OTP, {
      email: email.trim().toLowerCase(),
      isMock: false,
    });
  }

  // Step 2: validate the OTP. On success the backend returns a short-lived
  // reset token: { isOtpValidated: boolean, token?: string }.
  async validateResetOtp(email: string, otp: string) {
    return this.post<{ isOtpValidated: boolean; token?: string }>(
      API_ENDPOINTS.AUTH.VALIDATE_RESET_OTP,
      { email: email.trim().toLowerCase(), otp },
    );
  }

  // Step 3: set the new password. Authorized with the reset token from step 2
  // (passed explicitly — there is no admin_token while logged out, so the
  // request interceptor won't override this header).
  async resetStudioPassword(password: string, resetToken: string) {
    return this.post(
      API_ENDPOINTS.AUTH.RESET_STUDIO_PASSWORD,
      { password },
      { headers: { Authorization: `Bearer ${resetToken}` } },
    );
  }

  // User management. Studio owners get the same list scoped to their own
  // members (users with this studio in associatedStudios); admins get all.
  async getUsers(params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.USERS.STUDIO_LIST : API_ENDPOINTS.USERS.LIST;
    return this.get(endpoint, { params });
  }

  async getStudioUsersForMap() {
    return this.get(API_ENDPOINTS.USERS.STUDIO_MAP);
  }

  /**
   * Member search for the pickers (notifications, manual booking, pack assign).
   *
   * Studio-aware, like `getUsers`. `/users/admin/search` is AdminGuard-only and
   * unscoped, so a studio owner calling it got a 403 and an empty result list —
   * the notification "Specific Users" picker offered a search that could never
   * return anybody. Owners go to their own members endpoint instead, which the
   * backend scopes to the studio.
   *
   * Both branches are normalised to one shape so callers do not have to know
   * which one ran.
   */
  async searchUsers(q: string) {
    if (!this.isStudio()) {
      return this.get(API_ENDPOINTS.USERS.SEARCH(q));
    }
    const res: any = await this.getStudioMembers({ search: q, limit: 50 });
    const data = (res?.data || []).map((u: any) => ({
      _id: u._id,
      name: u.name,
      email: u.email,
      phoneNumber: u.phone || u.phoneNumber,
      city: u.city,
      // The studio projection names it profilePicture; the admin one, image.
      image: u.profilePicture || u.image,
    }));
    return { ...res, success: true, data, total: data.length };
  }

  async getUserById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.USERS.STUDIO_DETAIL(id) : API_ENDPOINTS.USERS.DETAIL(id);
    return this.get(endpoint);
  }

  // ── Invoices ──────────────────────────────────────────────────────────────
  async getInvoices(params?: any) {
    return this.get(API_ENDPOINTS.INVOICES.LIST, { params });
  }
  async getInvoiceNextNumber(studioId?: string) {
    return this.get(API_ENDPOINTS.INVOICES.NEXT_NUMBER, {
      params: studioId ? { studioId } : {},
    });
  }
  async getInvoiceRevenueSummary(params?: any) {
    return this.get(API_ENDPOINTS.INVOICES.REVENUE_SUMMARY, { params });
  }
  async updateInvoiceStatus(id: string, status: string, studioId?: string | null) {
    return this.patch(API_ENDPOINTS.INVOICES.UPDATE(id), { status }, this.atStudio(studioId));
  }
  // studioId pins a studio-role create: under "All locations" the switcher
  // sends __all__, which the server refuses for writes.
  async createInvoice(data: any, studioId?: string | null) {
    return this.post(API_ENDPOINTS.INVOICES.CREATE, data, this.atStudio(studioId));
  }
  async getInvoiceById(id: string) {
    return this.get(API_ENDPOINTS.INVOICES.DETAIL(id));
  }
  async updateInvoice(id: string, data: any, studioId?: string | null) {
    return this.patch(API_ENDPOINTS.INVOICES.UPDATE(id), data, this.atStudio(studioId));
  }
  async deleteInvoice(id: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.INVOICES.DELETE(id), this.atStudio(studioId));
  }

  async createUser(data: {
    name: string;
    email?: string;
    phoneNumber: string;
    age?: number;
    city?: string;
    gender?: string;
    registrationSource?: string;
    isMarketable?: boolean;
  }, studioId?: string | null) {
    // Studio owners create members under their own studio (scoped endpoint).
    // `studioId` = the location chosen under "All locations".
    const endpoint = this.isStudio() ? API_ENDPOINTS.USERS.STUDIO_CREATE : API_ENDPOINTS.USERS.CREATE;
    return this.post(endpoint, data, this.atStudio(studioId));
  }

  /**
   * `studioId` = a location the member is linked to AND the caller holds. The
   * server refuses an edit sent to a studio the member does not belong to, so
   * under "All locations" the account's own studio is only a last resort.
   */
  async updateUser(id: string, data: UpdateUserPayload, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.USERS.STUDIO_UPDATE(id) : API_ENDPOINTS.USERS.UPDATE(id);
    return this.patch(endpoint, data, studioId ? this.atStudio(studioId) : this.atOwnStudio());
  }

  async deleteUser(id: string, studioId?: string | null) {
    // Studio owners don't delete the user globally — they only detach the member
    // from their own studio (pull it from associatedStudios). `studioId` = the
    // location to detach from, chosen under "All locations".
    const endpoint = this.isStudio() ? API_ENDPOINTS.USERS.STUDIO_REMOVE(id) : API_ENDPOINTS.USERS.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  async bulkToggleUserMarketable(userIds: string[], isMarketable: boolean) {
    return this.patch(API_ENDPOINTS.USERS.BULK_TOGGLE_MARKETABLE, { userIds, isMarketable });
  }

  // Studio management
  async getStudios(params?: any) {
    return this.get(API_ENDPOINTS.STUDIOS.LIST, { params });
  }

  async getStudioById(id: string) {
    return this.get(API_ENDPOINTS.STUDIOS.DETAIL(id));
  }

  async updateStudio(id: string, data: any) {
    if (this.isStudio()) {
      // The studio route edits "my studio" — pinned to the one being edited,
      // since under "All locations" the switcher sends __all__ (refused for writes).
      return this.patch(API_ENDPOINTS.STUDIOS.UPDATE_STUDIO, data, this.atStudio(id));
    }
    return this.patch(API_ENDPOINTS.STUDIOS.UPDATE(id), data);
  }

  // ── Refunds ────────────────────────────────────────────────────────────
  async getRefunds(params?: { status?: string; studioId?: string; page?: number; limit?: number }) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.REFUNDS.LIST_STUDIO : API_ENDPOINTS.REFUNDS.LIST;
    return this.get(endpoint, { params });
  }

  /** Release a REQUESTED refund (or retry a FAILED one) to Razorpay. */
  // `studioId` = the refund's own location; pinned so "All locations"
  // (`__all__`, refused for writes) still lands at the right studio.
  async processRefund(id: string, studioId?: string | null) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.REFUNDS.PROCESS_STUDIO(id)
      : API_ENDPOINTS.REFUNDS.PROCESS(id);
    return this.post(endpoint, {}, this.atStudio(studioId));
  }

  async rejectRefund(id: string, reason?: string, studioId?: string | null) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.REFUNDS.REJECT_STUDIO(id)
      : API_ENDPOINTS.REFUNDS.REJECT(id);
    return this.post(endpoint, { reason }, this.atStudio(studioId));
  }

  async deleteStudio(id: string) {
    return this.delete(API_ENDPOINTS.STUDIOS.DELETE(id));
  }

  async toggleAdminPanelAccess(studioId: string, adminPanelAccess: boolean) {
    return this.patch(API_ENDPOINTS.STUDIOS.TOGGLE_ADMIN_PANEL_ACCESS(studioId), { adminPanelAccess });
  }

  /**
   * @deprecated Its revenue does not reconcile with the studio dashboards it
   * summarises — see the backend method's note. BrandDashboard calls
   * `getStudioTransactionStatsFor` per location instead. No caller remains.
   */
  async getOrgOverview(params?: { year?: number; month?: number; branchId?: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.ORG_OVERVIEW, { params });
  }

  // ── BRANDS ────────────────────────────────────────────────────────────────

  /**
   * The caller's own brand and the branches they hold.
   *
   * Takes NO studio id on purpose: the server decides what the caller may
   * see, and a client that passed its own id would be telling the server the
   * answer to the question it is asking.
   */
  /**
   * The app's branding, written to EVERY branch of the brand at once.
   *
   * The data still lives on each studio's `whitelabelConfig` — nothing was
   * migrated onto the Brand, because every reader in the platform resolves
   * branding from the studio. This endpoint just makes one edit reach all of
   * them, so two branches of one app cannot drift apart on a field that
   * describes a single app binary.
   *
   * Location-level fields (Razorpay, support contacts, SMTP) are rejected by
   * the server even if sent.
   */
  async setBrandWhitelabel(brandId: string, fields: Record<string, any>) {
    return this.patch(`/api/v1/brands/admin/${brandId}/whitelabel`, fields);
  }

  /**
   * Cached per session TOKEN. The switcher, the nav, the Dashboard and the
   * brand dashboard all ask this on every page, and the answer only changes
   * when someone's grants change. Keyed on the token so a logout / login (even
   * as a different account in the same tab) can never read the previous
   * account's locations. A failure is not cached.
   */
  private myBrandCache: { token: string | null; promise: Promise<any> } | null = null;

  async getMyBrand() {
    const token = localStorage.getItem('admin_token');
    if (this.myBrandCache && this.myBrandCache.token === token) {
      return this.myBrandCache.promise;
    }
    const promise = this.get(API_ENDPOINTS.BRANDS.MY).catch((e) => {
      this.myBrandCache = null;
      throw e;
    });
    this.myBrandCache = { token, promise };
    return promise;
  }

  async getBrands(params?: { search?: string; status?: string }) {
    return this.get(API_ENDPOINTS.BRANDS.LIST, { params });
  }

  async getBrand(brandId: string) {
    return this.get(API_ENDPOINTS.BRANDS.DETAIL(brandId));
  }

  async createBrand(data: {
    name: string;
    slug?: string;
    appSource?: string;
    adoptStudio?: string;
    logo?: string;
    supportEmail?: string;
    supportPhone?: string;
  }) {
    return this.post(API_ENDPOINTS.BRANDS.CREATE, data);
  }

  async updateBrand(
    brandId: string,
    data: {
      name?: string;
      appSource?: string;
      defaultStudio?: string;
      logo?: string;
      supportEmail?: string;
      supportPhone?: string;
      status?: 'active' | 'suspended';
      sharing?: {
        packs?: boolean;
        benefits?: boolean;
        referrals?: boolean;
        announcements?: boolean;
      };
      // Write-only. No read ever returns the secret.
      razorpayKeyId?: string;
      razorpayKeySecret?: string;
    },
  ) {
    return this.patch(API_ENDPOINTS.BRANDS.UPDATE(brandId), data);
  }

  /** `email`/`password` are OPTIONAL — a branch is a place, not a login. */
  async createBranch(
    brandId: string,
    data: {
      studioName: string;
      branchName?: string;
      branchCode?: string;
      phoneNumber?: string;
      address?: string;
      city?: string;
      state?: string;
      pincode?: string;
      images?: string[];
      coordinates?: number[];
      email?: string;
      /** Leave out to have one generated (returned once as `login.password`). */
      password?: string;
      /** Use the default location's Razorpay account. Off unless chosen. */
      copyPaymentAccount?: boolean;
    },
  ) {
    return this.post(API_ENDPOINTS.BRANDS.CREATE_BRANCH(brandId), data);
  }

  /** The brand's locations for a studio, and whether the caller may add an instructor at each (super admin: all). */
  /**
   * Create at several locations in one request. Pinned to one of them: under
   * "All locations" the switcher would send the read-only `__all__`, which the
   * server refuses for any write.
   */
  async createClassesAtLocations(
    items: { studioId: string; class: any; sourceClassId?: string }[],
  ) {
    return this.post(API_ENDPOINTS.MULTI_LOCATION.CLASSES, { items }, this.atStudio(items[0]?.studioId));
  }

  async createSessionsAtLocations(body: {
    locations: { studioId: string; sessions: any[] }[];
    allowDuplicate?: boolean;
  }) {
    return this.post(API_ENDPOINTS.MULTI_LOCATION.SESSIONS, body, this.atStudio(body.locations[0]?.studioId));
  }

  async getBrandLocationsForStudio(studioId: string) {
    return this.get(API_ENDPOINTS.BRANDS.STUDIO_BRAND_LOCATIONS(studioId));
  }

  /** Where an instructor teaches, plus every brand location and whether the caller may change it. */
  async getInstructorLocations(instructorId: string) {
    return this.get(API_ENDPOINTS.BRANDS.INSTRUCTOR_LOCATIONS(instructorId));
  }

  /** Set the locations an instructor teaches at. Their login's access follows. */
  async setInstructorLocations(instructorId: string, studioIds: string[], pinStudioId?: string | null) {
    // A write must name ONE location — under "All locations" the switcher
    // sends __all__, which the server refuses for writes. The server checks
    // the caller's role at every location being changed regardless.
    return this.put(
      API_ENDPOINTS.BRANDS.INSTRUCTOR_LOCATIONS(instructorId),
      { studioIds },
      this.atStudio(pinStudioId),
    );
  }

  /**
   * Create a location's owner login, or set a new password for it. With no
   * password the server generates one and returns it once.
   */
  async setBranchLogin(
    brandId: string,
    branchId: string,
    data: { email?: string; password?: string; loginId?: string },
  ) {
    return this.put(API_ENDPOINTS.BRANDS.BRANCH_LOGIN(brandId, branchId), data);
  }

  async setBranchActive(brandId: string, branchId: string, isActive: boolean) {
    return this.patch(API_ENDPOINTS.BRANDS.SET_BRANCH_ACTIVE(brandId, branchId), {
      isActive,
    });
  }

  /** Studios the brand could adopt — already filtered server-side. */
  async getAdoptableStudios(brandId: string, search?: string) {
    return this.get(API_ENDPOINTS.BRANDS.ADOPTABLE(brandId), {
      params: search ? { search } : undefined,
    });
  }

  async adoptStudioIntoBrand(brandId: string, studioId: string) {
    return this.post(API_ENDPOINTS.BRANDS.ADOPT_STUDIO(brandId, studioId), {});
  }

  /** Gives the studio a brand of its own; never leaves it brandless. */
  async detachStudioFromBrand(brandId: string, studioId: string) {
    return this.delete(API_ENDPOINTS.BRANDS.DETACH_STUDIO(brandId, studioId));
  }

  /** Everyone grantable in this brand, with the role each already holds. */
  async getBrandPeople(brandId: string) {
    return this.get(API_ENDPOINTS.BRANDS.PEOPLE(brandId));
  }

  async getBrandGrants(brandId: string) {
    return this.get(API_ENDPOINTS.BRANDS.GRANTS(brandId));
  }

  async grantBrandAccess(
    brandId: string,
    data: {
      kind: 'staff' | 'studio';
      userId: string;
      role: 'brand_owner' | 'studio_owner' | 'manager' | 'instructor';
      /** Omit both for a brand-wide grant. One row is written per location. */
      studio?: string;
      studios?: string[];
    },
  ) {
    return this.post(API_ENDPOINTS.BRANDS.GRANTS(brandId), data);
  }

  /**
   * "They work at these locations" — reconciled server-side, so a partial
   * application is not possible.
   */
  async setBrandGrantLocations(
    brandId: string,
    data: {
      kind: 'staff' | 'studio';
      userId: string;
      role: 'studio_owner' | 'manager' | 'instructor';
      studios: string[];
    },
  ) {
    return this.put(API_ENDPOINTS.BRANDS.GRANT_LOCATIONS(brandId), data);
  }

  async revokeBrandAccess(brandId: string, grantId: string) {
    return this.delete(API_ENDPOINTS.BRANDS.REVOKE_GRANT(brandId, grantId));
  }

  // Read-only Purple Pilates / studio operations overview (daily/weekly, IST).
  async getStudioOverview(params?: { studioId?: string; range?: 'day' | 'week'; date?: string }) {
    return this.get('/api/v1/admin/studio-overview', { params });
  }

  async getStudioEvents(studioId: string, params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.EVENTS.LIST_STUDIO : API_ENDPOINTS.STUDIOS.EVENTS(studioId);
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  // `pinStudioId`: pin the read to one location (e.g. the studio page, which
  // is always about ONE studio — never the `__all__` aggregate).
  async getStudioSessions(studioId: string, params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.SESSIONS.LIST_STUDIO : API_ENDPOINTS.STUDIOS.SESSIONS(studioId);
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  async getStudioRevenue(studioId: string, params?: { startDate?: string; endDate?: string }) {
    if (this.isStudio()) {
      return this.get(API_ENDPOINTS.STUDIOS.REVENUE_STUDIO, { params });
    }
    return this.get(API_ENDPOINTS.STUDIOS.REVENUE(studioId), { params });
  }

  async getAllStudiosRevenue(params?: { startDate?: string; endDate?: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.ALL_REVENUE, { params });
  }

  // Correct monthly revenue series (booking BOOKED/CHECKED_IN + pack purchases,
  // excludes subscription/cancelled) for the logged-in studio. type: 'monthly' | 'yearly'.
  async getEarningsSummary(params?: { type?: string; branchId?: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.EARNINGS_SUMMARY, { params: { type: 'monthly', ...params } });
  }

  async getStudioRevenueDetails(studioId: string, params?: { startDate?: string; endDate?: string }) {
    if (this.isStudio()) {
      return this.get(API_ENDPOINTS.STUDIOS.REVENUE_DETAILS_STUDIO, { params });
    }
    return this.get(API_ENDPOINTS.STUDIOS.REVENUE_DETAILS(studioId), { params });
  }

  async generateStudioInvoice(studioId: string, params?: { startDate?: string; endDate?: string; discountAmount?: number; discountLabel?: string; adjustmentAmount?: number; adjustmentLabel?: string }): Promise<Blob> {
    // Use axios instance to maintain consistency with interceptors and token handling
    const response = await this.instance.get(`/api/v1/studios/admin/revenue/${studioId}/invoice`, {
      params,
      responseType: 'blob', // Important: set responseType to 'blob' for PDF downloads
    });
    
    return response.data; // Returns PDF file
  }

  async downloadTransactionReceipt(transactionId: string): Promise<Blob> {
    const response = await this.instance.get(`/api/v1/transactions/admin/${transactionId}/receipt`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Booking management
  async getBookings(params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.BOOKINGS.LIST_STUDIO : API_ENDPOINTS.BOOKINGS.LIST;
    return this.get(endpoint, { params });
  }

  // Monthly booking counts for the dashboard trend chart, defaulting to
  // platform-launch-to-now server-side when no range is passed. Same URL for
  // admin and studio roles — the backend scopes it by studio via the auth
  // token, no isStudio() branching needed here.
  async getBookingsTrend(params?: { startDate?: string; endDate?: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.BOOKINGS_TREND, { params });
  }

  // Super admin dashboard: non-cancelled Sweat Social bookings created in the range.
  async getSweatSocialBookingsCount(params: { startDate: string; endDate: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.SS_BOOKINGS_COUNT, { params });
  }

  async getEventBookings(eventId: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.BOOKINGS.LIST_STUDIO : API_ENDPOINTS.BOOKINGS.LIST;
    return this.get(endpoint, { params: { eventId, pageSize: 500 } });
  }

  async getBookingById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.BOOKINGS.DETAIL_STUDIO(id) : API_ENDPOINTS.BOOKINGS.DETAIL(id);
    return this.get(endpoint);
  }

  async updateBooking(id: string, data: any) {
    return this.put(API_ENDPOINTS.BOOKINGS.UPDATE(id), data);
  }

  async cancelBooking(id: string, studioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.CANCEL(id), undefined, this.atStudio(studioId));
  }

  async cancelBookingAdmin(id: string, studioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.CANCEL_ADMIN(id), undefined, this.atStudio(studioId));
  }

  async getStudioCustomers(params?: any) {
    return this.get(API_ENDPOINTS.BOOKINGS.STUDIO_CUSTOMERS, { params });
  }

  async getStudioMembers(params?: any) {
    return this.get(API_ENDPOINTS.USERS.STUDIO_MEMBERS, { params });
  }

  async getStudioHealthForms(params?: any) {
    return this.get(API_ENDPOINTS.USERS.STUDIO_HEALTH_FORMS, { params });
  }

  async deleteBooking(id: string) {
    return this.delete(API_ENDPOINTS.BOOKINGS.DELETE(id));
  }

  async getSessionWaitlists(date?: string) {
    return this.get(API_ENDPOINTS.BOOKINGS.SESSION_WAITLISTS, { params: date ? { date } : {} });
  }

  /** Blockers + overridable warnings for adding this member to the waitlist. */
  // studioId (all five waitlist calls): the session's own location, so the
  // board works under "All locations" (writes there are refused otherwise).
  async checkWaitlistAdd(sessionId: string, userId: string, studioId?: string | null) {
    return this.get(API_ENDPOINTS.BOOKINGS.WAITLIST_CHECK_ADD(sessionId), { params: { userId }, ...this.atStudio(studioId) });
  }

  /** Adds at the end of the queue. confirm=true overrides the warnings. */
  async addToWaitlist(sessionId: string, userId: string, confirm = false, studioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.WAITLIST_ADD(sessionId), { userId, confirm }, this.atStudio(studioId));
  }

  /** entryIds = every active entry, first to last. */
  async reorderWaitlist(sessionId: string, entryIds: string[], studioId?: string | null) {
    return this.patch(API_ENDPOINTS.BOOKINGS.WAITLIST_REORDER(sessionId), { entryIds }, this.atStudio(studioId));
  }

  async removeFromWaitlist(entryId: string, reason?: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.BOOKINGS.WAITLIST_REMOVE(entryId), { params: reason ? { reason } : {}, ...this.atStudio(studioId) });
  }

  /** "Book now" for one queued member. confirm=true outside the auto window. */
  async bookFromWaitlist(entryId: string, confirm = false, studioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.WAITLIST_BOOK(entryId), { confirm }, this.atStudio(studioId));
  }

  // pinStudioId on the manual-booking / pack-assign user lookups: they are
  // POSTs, so under "All locations" the switcher's __all__ is refused.
  async checkUserByPhone(phoneNumber: string, pinStudioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.MANUAL_CHECK_USER, { phoneNumber }, this.atStudio(pinStudioId));
  }

  async createUserForManualBooking(data: { phoneNumber: string; name: string; email: string; age: number; gender?: string }, pinStudioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.MANUAL_CREATE_USER, data, this.atStudio(pinStudioId));
  }

  // pinStudioId: a studio role resolves these from the x-studio-id header, not
  // the `studioId` param — pass it under "All locations" so the list is the
  // chosen location's, not the sign-in branch's.
  async getSessionsForManualBooking(classTypeId?: string, studioId?: string, date?: string, category?: string, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.BOOKINGS.MANUAL_GET_SESSIONS, { params: { classTypeId, studioId, date, category }, ...this.atStudio(pinStudioId) });
  }

  async getUserActivePacksForBooking(userId: string, studioId: string, sessionType?: string, includeUpcoming?: boolean, pinStudioId?: string | null) {
    return this.get(`/api/v1/packs/admin/studio/user-active-packs`, {
      params: { userId, studioId, sessionType, includeUpcoming: includeUpcoming ? 'true' : undefined },
      ...this.atStudio(pinStudioId),
    });
  }

  async createManualBooking(data: {
    userId: string;
    sessionId?: string;
    eventId?: string;
    isPaid: boolean;
    basePrice: number;
    taxes?: number;
    modeOfPayment?: string;
    studioId?: string;
    purchasedPackId?: string;
    bookingStatus?: string;
    quantity?: number;
    appSource?: string;
    /**
     * Record as the member's trial class. Sessions only, and never together
     * with purchasedPackId — the backend rejects that pairing, since a trial
     * is itself the payment method. Eligibility is enforced server-side.
     */
    isTrial?: boolean;
    /**
     * Staff confirmed the trial despite an eligibility warning. Audit-logged.
     * Does not bypass the hard rules (pack / event / trial-disabled session).
     */
    overrideTrialEligibility?: boolean;
    /**
     * The bike, for a class that runs in a room. Omit for a class with no room
     * — the server then books by headcount exactly as before. When the class
     * IS seated and no id is sent, the server auto-assigns the first free
     * machine rather than leaving the member without one.
     */
    seatId?: string;
  }, pinStudioId?: string | null) {
    // pinStudioId: the session's/event's location — under "All locations" the
    // switcher sends __all__, which the server refuses for writes.
    return this.post(API_ENDPOINTS.BOOKINGS.MANUAL_CREATE_BOOKING, data, this.atStudio(pinStudioId));
  }

  /** Classes a purchased pack can pay for, each with its weekly slots. */
  async getBulkPackBookingOptions(purchasedPackId: string, studioId?: string) {
    return this.get(API_ENDPOINTS.BOOKINGS.BULK_PACK_OPTIONS, { params: { purchasedPackId, studioId } });
  }

  /** The dates a bulk booking would take, each marked bookable or why not. Writes nothing. */
  async previewBulkPackBooking(data: {
    purchasedPackId: string;
    /** Class for slots that do not name their own. */
    classId?: string;
    slots: { classId?: string; weekday: number; time: string }[];
    startDate?: string;
    /** Omit to book every matching session up to endDate / the pack's expiry. */
    count?: number;
    /** Inclusive last day, YYYY-MM-DD. */
    endDate?: string;
    studioId?: string;
  }) {
    return this.post(API_ENDPOINTS.BOOKINGS.BULK_PACK_PREVIEW, data);
  }

  /** Books the chosen sessions on the pack. Dates that fail come back in `failed`. */
  async createBulkPackBooking(data: {
    purchasedPackId: string;
    sessionIds: string[];
    notifyMember?: boolean;
    studioId?: string;
  }) {
    return this.post(API_ENDPOINTS.BOOKINGS.BULK_PACK_CREATE, data);
  }

  /** The member's bookings paid from this pack; only BOOKED ones can be cancelled. */
  async getBulkPackBookings(purchasedPackId: string, studioId?: string) {
    return this.get(API_ENDPOINTS.BOOKINGS.BULK_PACK_BOOKINGS, { params: { purchasedPackId, studioId } });
  }

  /**
   * Cancels ticked BOOKED bookings of this pack; each gives its class back.
   * Call with dryRun first and show ITS count in the confirm.
   */
  async cancelBulkPackBookings(data: {
    purchasedPackId: string;
    bookingIds: string[];
    dryRun: boolean;
    studioId?: string;
  }): Promise<{
    dryRun: boolean;
    cancelledCount: number;
    skipped: { bookingId: string; reason: string }[];
    classesLeft: number | null;
  }> {
    return this.post(API_ENDPOINTS.BOOKINGS.BULK_PACK_CANCEL, data);
  }

  async createAdminWalkIn(data: {
    userId: string;
    sessionDatetime: string;
    bookingStatus: string;
    modeOfPayment: string;
    purchasedPackId?: string;
    /**
     * Which real class the walk-in counts as. REQUIRED when the chosen pack is a
     * combo pack: a walk-in has no class of its own, so the pack cannot tell
     * which per-class quota to spend and the debit fails without it.
     */
    packClassId?: string;
    basePrice?: number;
    isPaid?: boolean;
    studioId?: string;
  }, pinStudioId?: string | null) {
    const { studioId, ...body } = data;
    const url = studioId
      ? `${API_ENDPOINTS.BOOKINGS.ADMIN_WALK_IN}?studioId=${studioId}`
      : API_ENDPOINTS.BOOKINGS.ADMIN_WALK_IN;
    return this.post(url, body, this.atStudio(pinStudioId));
  }

  async exportBookingsCSV(params?: any): Promise<Blob> {
    // Use axios instance to maintain consistency with interceptors and token handling
    const response = await this.instance.get(API_ENDPOINTS.BOOKINGS.EXPORT_CSV, {
      params,
      responseType: 'blob', // Important: set responseType to 'blob' for file downloads
    });
    
    return response.data; // Returns Excel file (.xlsx) with styling
  }

  // Class management
  async getClasses(studioId: string, params?: any) {
    return this.get(API_ENDPOINTS.CLASSES.LIST_BY_STUDIO(studioId), { params });
  }

  // pinStudioId: one location's classes (studio roles). Under "All locations"
  // the list otherwise spans every location the caller holds.
  async getAllClasses(params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.CLASSES.LIST_STUDIO : API_ENDPOINTS.CLASSES.LIST;
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  async getClassById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.CLASSES.DETAIL_STUDIO(id) : API_ENDPOINTS.CLASSES.DETAIL(id);
    return this.get(endpoint);
  }

  // Active Sessions management
  // pinStudioId: read ONE location's sessions (studio roles). Under "All
  // locations" the list otherwise spans every location the caller holds.
  async getActiveSessionsByDate(params?: any, pinStudioId?: string | null) {
    if (this.isStudio()) {
      return this.get(API_ENDPOINTS.SESSIONS.LIST_STUDIO, { params, ...this.atStudio(pinStudioId) });
    }
    return this.get(API_ENDPOINTS.SESSIONS.ACTIVE_BY_DATE, { params });
  }

  // All sessions for a date (past + future, unlike getActiveSessionsByDate which
  // only returns live/upcoming ones) — used by the bookings calendar view so
  // admins can review sessions that already happened.
  async getAllSessionsByDate(params?: any) {
    if (this.isStudio()) {
      return this.get(API_ENDPOINTS.SESSIONS.LIST_STUDIO, { params });
    }
    return this.get(API_ENDPOINTS.SESSIONS.ALL_BY_DATE, { params });
  }

  // Get all sessions including past ones
  async getAllSessions(params?: any) {
    return this.get(API_ENDPOINTS.SESSIONS.LIST, { params });
  }

  async createClass(studioId: string, data: any) {
    if (this.isStudio()) {
      return this.post(
        API_ENDPOINTS.CLASSES.CREATE_STUDIO,
        data,
        this.atStudio(studioId),
      );
    }
    return this.post(API_ENDPOINTS.CLASSES.CREATE(studioId), data);
  }

  async updateClass(id: string, data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.CLASSES.UPDATE_STUDIO(id) : API_ENDPOINTS.CLASSES.UPDATE(id);
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  /** This class's upcoming sessions an edit could reach (asked before saving). */
  async getClassUpdateImpact(id: string, studioId?: string | null): Promise<{
    upcomingSessions: number;
    bookedUpcomingSessions: number;
    ownInstructorSessions: number;
    ownPriceSessions: number;
  }> {
    return this.get(API_ENDPOINTS.CLASSES.UPDATE_IMPACT(id), this.atStudio(studioId));
  }

  async deleteClass(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.CLASSES.DELETE_STUDIO(id) : API_ENDPOINTS.CLASSES.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  // Event management
  async getEvents(params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.EVENTS.LIST_STUDIO : API_ENDPOINTS.EVENTS.LIST;
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  async getEventById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.EVENTS.DETAIL_STUDIO(id) : API_ENDPOINTS.EVENTS.DETAIL(id);
    return this.get(endpoint);
  }

  async getSessionById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.SESSIONS.DETAIL_STUDIO(id) : API_ENDPOINTS.SESSIONS.DETAIL(id);
    return this.get(endpoint);
  }

  async createEvent(studioId: string, data: any) {
    if (this.isStudio()) {
      return this.post(
        API_ENDPOINTS.EVENTS.CREATE_STUDIO,
        data,
        this.atStudio(studioId),
      );
    }
    return this.post(API_ENDPOINTS.EVENTS.CREATE(studioId), data);
  }

  async updateEvent(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.EVENTS.UPDATE_STUDIO : API_ENDPOINTS.EVENTS.UPDATE;
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  async deleteEvent(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.EVENTS.DELETE_STUDIO(id) : API_ENDPOINTS.EVENTS.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  async getEventShareLinks(eventId: string) {
    return this.get(API_ENDPOINTS.EVENTS.SHARE_LINKS(eventId));
  }

  // Merchandise management
  async getMerchandise(params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.MERCHANDISE.LIST_STUDIO : API_ENDPOINTS.MERCHANDISE.LIST;
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  async createMerchandise(studioId: string, data: any) {
    if (this.isStudio()) return this.post(API_ENDPOINTS.MERCHANDISE.CREATE_STUDIO, data, this.atStudio(studioId));
    return this.post(API_ENDPOINTS.MERCHANDISE.CREATE(studioId), data);
  }

  async updateMerchandise(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.MERCHANDISE.UPDATE_STUDIO : API_ENDPOINTS.MERCHANDISE.UPDATE;
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  async deleteMerchandise(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.MERCHANDISE.DELETE_STUDIO(id) : API_ENDPOINTS.MERCHANDISE.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  /**
   * Enquiries for one studio.
   *
   * `studioId` is REQUIRED for super-admins — the endpoint is studio-scoped and
   * 400s without it. This previously took only `merchandiseId`, so the page sent
   * no studio, every super-admin request failed, and the error was swallowed
   * into an empty "No enquiries yet" state.
   */
  async getMerchandiseEnquiries(params?: { studioId?: string; merchandiseId?: string }) {
    return this.get(API_ENDPOINTS.MERCHANDISE.ENQUIRIES, { params: params ?? {} });
  }

  // ── Merchandise inventory ──────────────────────────────────────────────────

  /** Restock / write-off / recount. The only way stock changes outside an order. */
  async adjustMerchandiseStock(
    merchandiseId: string,
    data: { variantSku?: string; delta: number; reason: string; note?: string },
    studioId?: string,
    pinStudioId?: string | null,
  ) {
    return this.post(API_ENDPOINTS.MERCHANDISE.ADJUST_STOCK(merchandiseId), data, {
      params: studioId ? { studioId } : {},
      ...this.atStudio(pinStudioId),
    });
  }

  /** Movement history for a product — "where did the stock go?" */
  async getMerchandiseStockLedger(merchandiseId: string, params?: any) {
    return this.get(API_ENDPOINTS.MERCHANDISE.STOCK_LEDGER(merchandiseId), { params });
  }

  async getMerchandiseLowStock(params?: { studioId?: string; threshold?: number }) {
    return this.get(API_ENDPOINTS.MERCHANDISE.LOW_STOCK, { params: params ?? {} });
  }

  // ── Merchandise orders ─────────────────────────────────────────────────────

  async getMerchandiseOrders(params?: any) {
    return this.get(API_ENDPOINTS.MERCHANDISE.ORDERS, { params: params ?? {} });
  }

  /** Advances an order: PLACED → PACKED → READY_FOR_PICKUP → COLLECTED. */
  async updateMerchandiseOrderStatus(
    orderId: string,
    data: { status: string; note?: string },
    studioId?: string,
    // The order's own location, for a studio role — under "All locations" the
    // switcher sends __all__, which the server refuses for writes.
    pinStudioId?: string | null,
  ) {
    return this.patch(API_ENDPOINTS.MERCHANDISE.ORDER_STATUS(orderId), data, {
      params: studioId ? { studioId } : {},
      ...this.atStudio(pinStudioId),
    });
  }

  /** Refunds a CANCELLED order. Studio-admin only, server-side. */
  async refundMerchandiseOrder(
    orderId: string,
    data: { amount?: number; reason?: string },
    studioId?: string,
    pinStudioId?: string | null,
  ) {
    return this.post(API_ENDPOINTS.MERCHANDISE.ORDER_REFUND(orderId), data, {
      params: studioId ? { studioId } : {},
      ...this.atStudio(pinStudioId),
    });
  }

  /** Merch revenue — a parallel ledger, deliberately not part of studio payouts. */
  async getMerchandiseRevenue(params?: { studioId?: string; from?: string; to?: string }) {
    return this.get(API_ENDPOINTS.MERCHANDISE.REVENUE, { params: params ?? {} });
  }

  async createSession(studioId: string, data: any) {
    if (this.isStudio()) {
      return this.post(API_ENDPOINTS.SESSIONS.CREATE_STUDIO, data);
    }
    return this.post(API_ENDPOINTS.SESSIONS.CREATE(studioId), data);
  }

  async createMultipleSessions(
    studioId: string,
    data: { sessions: any[]; allowDuplicate?: boolean; allowPastSessions?: boolean },
  ) {
    if (this.isStudio()) {
      // Pinned: a session belongs to ONE branch, and under "All locations"
      // the switcher would otherwise send the `__all__` sentinel, which the
      // server refuses for writes. The page names the branch (see
      // StudioSessionsPage) and it travels with the request.
      return this.post(
        API_ENDPOINTS.SESSIONS.CREATE_MULTIPLE_STUDIO,
        data,
        this.atStudio(studioId),
      );
    }
    return this.post(API_ENDPOINTS.SESSIONS.CREATE_MULTIPLE(studioId), data);
  }

  async bulkCreateByRange(studioId: string, data: {
    class: string;
    startDate: string;
    endDate: string;
    times: string[];
    slots: number;
    isLive: boolean;
    onlyForFemales?: boolean;
    daysOfWeek?: number[];
    isTrialEnabled?: boolean;
    trialPrice?: number;
    allowDuplicate?: boolean;
    /** Occurrences the operator unchecked in the duplicate prompt. */
    skip?: { class: string; date: string }[];
  }) {
    if (this.isStudio()) {
      return this.post(API_ENDPOINTS.SESSIONS.BULK_CREATE_BY_RANGE_STUDIO, data, { timeout: BULK_RANGE_TIMEOUT_MS, ...this.atStudio(studioId) });
    }
    return this.post(API_ENDPOINTS.SESSIONS.BULK_CREATE_BY_RANGE(studioId), data, { timeout: BULK_RANGE_TIMEOUT_MS });
  }

  async bulkDeleteByRange(studioId: string, data: {
    class: string;
    startDate: string;
    endDate: string;
    daysOfWeek?: number[];
    /** Start times (HH:mm IST) to target. Omit to target every time in the range. */
    times?: string[];
    trialOnly?: boolean;
    /**
     * The exact sessions ticked in the preview. The backend still applies the
     * filter, so these can only NARROW the set, never widen it. Always send them
     * for a preview-driven delete — without them the request degrades to "every
     * session of this class in the date span".
     */
    sessionIds?: string[];
    includePast?: boolean;
    dryRun?: boolean;
  }) {
    if (this.isStudio()) {
      return this.delete(API_ENDPOINTS.SESSIONS.BULK_DELETE_BY_RANGE_STUDIO, { data, timeout: BULK_RANGE_TIMEOUT_MS, ...this.atStudio(studioId) });
    }
    return this.delete(API_ENDPOINTS.SESSIONS.BULK_DELETE_BY_RANGE(studioId), { data, timeout: BULK_RANGE_TIMEOUT_MS });
  }

  /** Read-only: what does this range filter match? Drives the bulk workspace. */
  async bulkPreviewByRange(studioId: string, filter: any) {
    if (this.isStudio()) {
      return this.post(API_ENDPOINTS.SESSIONS.BULK_PREVIEW_BY_RANGE_STUDIO, filter, this.atStudio(studioId));
    }
    return this.post(API_ENDPOINTS.SESSIONS.BULK_PREVIEW_BY_RANGE(studioId), filter);
  }

  /**
   * Bulk edit. `sessionIds` narrows to exactly what the operator ticked in the
   * preview; `dryRun` reports the outcome without writing (the confirm step
   * always dry-runs first).
   */
  async bulkUpdateByRange(studioId: string, data: {
    filter: any;
    changes: any;
    sessionIds?: string[];
    dryRun?: boolean;
    notifyMembers?: boolean;
  }) {
    if (this.isStudio()) {
      return this.patch(API_ENDPOINTS.SESSIONS.BULK_UPDATE_BY_RANGE_STUDIO, data, {
        timeout: BULK_RANGE_TIMEOUT_MS,
        ...this.atStudio(studioId),
      });
    }
    return this.patch(API_ENDPOINTS.SESSIONS.BULK_UPDATE_BY_RANGE(studioId), data, { timeout: BULK_RANGE_TIMEOUT_MS });
  }

  async updateSession(id: string, data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.SESSIONS.UPDATE_STUDIO(id) : API_ENDPOINTS.SESSIONS.UPDATE(id);
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  async deleteSession(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.SESSIONS.DELETE_STUDIO(id) : API_ENDPOINTS.SESSIONS.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  async deleteSessionsByDate(date: string) {
    return this.delete(API_ENDPOINTS.SESSIONS.DELETE_BY_DATE, {
      params: { date },
    });
  }

  async getClassTypes(params?: any) {
    return this.get(API_ENDPOINTS.CLASSES.ALL_TYPES, { params });
  }

  // Class type management — super_admin only (SuperAdminGuard on the backend)
  async createClassType(data: any) {
    return this.post(API_ENDPOINTS.CLASSES.CREATE_TYPE, data);
  }

  async updateClassType(id: string, data: any) {
    return this.patch(API_ENDPOINTS.CLASSES.UPDATE_TYPE, { ...data, classTypeId: id });
  }

  async deleteClassType(id: string) {
    return this.delete(API_ENDPOINTS.CLASSES.DELETE_TYPE(id));
  }

  async getStudioClasses(studioId: string, params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.CLASSES.LIST_STUDIO : API_ENDPOINTS.CLASSES.LIST_BY_STUDIO(studioId);
    return this.get(endpoint, { params, ...this.atStudio(studioId) });
  }

  /**
   * ── THE STUDIO ID IS NOT DECORATIVE ───────────────────────────────────────
   *
   * For a studio role this routed to LIST_STUDIO and threw `studioId` away,
   * letting the interceptor scope it to whatever the switcher said. That was
   * harmless while the switcher always named one branch.
   *
   * It stopped being harmless the moment a form could target a DIFFERENT
   * branch than the one being viewed: picking "Yelahanka" in the class dialog
   * offered Koramangala's instructors and saved without complaint — a class
   * at one branch taught by someone who does not work there.
   */
  async getInstructorsByStudio(studioId: string, params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.INSTRUCTORS.LIST_STUDIO : API_ENDPOINTS.INSTRUCTORS.LIST(studioId);
    return this.get(endpoint, { params, ...this.atStudio(studioId) });
  }

  // Transaction management
  /** Same-class future sessions this booking can be moved into (staff, 30-day window). */
  async getRescheduleOptions(bookingId: string) {
    return this.get(API_ENDPOINTS.BOOKINGS.RESCHEDULE_OPTIONS(bookingId));
  }

  /**
   * @param seatId the bike chosen in the TARGET session's room. Omit for a
   *   class with no room — the server then books by headcount exactly as
   *   before. For a seated class the id is honoured exactly: an unusable pick
   *   is refused rather than silently swapped for another machine.
   */
  async rescheduleBooking(bookingId: string, sessionId: string, seatId?: string, studioId?: string | null) {
    return this.post(API_ENDPOINTS.BOOKINGS.RESCHEDULE(bookingId), {
      sessionId,
      ...(seatId ? { seatId } : {}),
    }, this.atStudio(studioId));
  }

  async getTransactions(params?: any) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.TRANSACTIONS.LIST_STUDIO
      : API_ENDPOINTS.TRANSACTIONS.LIST;
    return this.get(endpoint, { params });
  }

  // Pinned to the row's own studio, so it still lands under "All locations".
  async updateTransactionPayment(
    transactionId: string,
    body: { amount?: number; modeOfPayment?: 'cash' | 'online' },
    studioId?: any,
  ) {
    return this.patch(
      API_ENDPOINTS.TRANSACTIONS.UPDATE_PAYMENT(transactionId),
      body,
      this.atStudio(studioId),
    );
  }

  async cancelInvoice(transactionId: string, studioId?: any) {
    return this.patch(
      API_ENDPOINTS.TRANSACTIONS.CANCEL_INVOICE(transactionId),
      {},
      this.atStudio(studioId),
    );
  }

  /**
   * The same stats a studio owner's own dashboard reads, for ONE named
   * location.
   *
   * The location is pinned per request rather than through the stored
   * selection, because the brand dashboard asks about every location while
   * "All" is selected — and the stored selection is then deliberately absent.
   */
  async getStudioTransactionStatsFor(studioId: string, params?: any) {
    return this.get(API_ENDPOINTS.TRANSACTIONS.STATS_STUDIO, {
      params,
      headers: { 'x-studio-id': studioId },
    });
  }

  /** Bookings for ONE named location, same endpoint the studio dashboard uses. */
  async getBookingsFor(studioId: string, params?: any) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.BOOKINGS.LIST_STUDIO
      : API_ENDPOINTS.BOOKINGS.LIST;
    return this.get(endpoint, { params, headers: { 'x-studio-id': studioId } });
  }

  async getTransactionStats(params?: any) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.TRANSACTIONS.STATS_STUDIO
      : API_ENDPOINTS.TRANSACTIONS.STATS;
    return this.get(endpoint, { params });
  }

  // Drill-down: the transaction rows behind one split-bar segment of a stats
  // breakdown popup. Admins use the global endpoint; studio owners get a
  // studio-scoped variant pinned to their own studio + own-studio buckets.
  // `bucket` is one of
  // ownGateway | sweatSocialGateway | ownAppCash | ssRoutedCash |
  // partnerInApp | partnerViaSweatSocial | sweatSocialDirect (Razorpay captures)
  // | sweatSocialDirectCash (cash / manual) | studioRazorpayAll (studio-scoped
  // ownGateway + sweatSocialGateway) | studioCashAll (studio-scoped ownAppCash
  // + ssRoutedCash). The sweatSocialDirect* buckets are global (no studioId);
  // their `source` optionally narrows to one channel ('app' | 'website' | 'admin').
  async getStatsSegmentTransactions(params: {
    studioId?: string;
    source?: string;
    bucket:
      | 'ownGateway'
      | 'sweatSocialGateway'
      | 'ownAppCash'
      | 'ssRoutedCash'
      | 'partnerInApp'
      | 'partnerViaSweatSocial'
      | 'sweatSocialDirect'
      | 'sweatSocialDirectCash'
      | 'studioRazorpayAll'
      | 'studioCashAll'
      | 'cancelled'
      | 'allRazorpay'
      | 'allCash';
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.TRANSACTIONS.STATS_SEGMENT_STUDIO
      : API_ENDPOINTS.TRANSACTIONS.STATS_SEGMENT;
    return this.get(endpoint, { params });
  }

  async getTransactionById(id: string) {
    return this.get(API_ENDPOINTS.TRANSACTIONS.DETAIL(id));
  }

  // ── Seat layouts (rooms) ────────────────────────────────────────────────
  // Admin routes take an explicit studioId; studio roles are pinned by the JWT
  // and the server ignores any studio they send.
  async getSeatLayouts(studioId: string) {
    // Same reason as getInstructorsByStudio: a room belongs to one branch.
    return this.get(
      this.isStudio()
        ? API_ENDPOINTS.SEAT_LAYOUTS.LIST_STUDIO
        : API_ENDPOINTS.SEAT_LAYOUTS.LIST_ADMIN(studioId),
      this.atStudio(studioId),
    );
  }

  async createSeatLayout(studioId: string, data: any) {
    return this.post(
      this.isStudio()
        ? API_ENDPOINTS.SEAT_LAYOUTS.CREATE_STUDIO
        : API_ENDPOINTS.SEAT_LAYOUTS.CREATE_ADMIN(studioId),
      data,
      this.atStudio(studioId),
    );
  }

  async updateSeatLayout(id: string, data: any, studioId?: string | null) {
    return this.patch(
      this.isStudio()
        ? API_ENDPOINTS.SEAT_LAYOUTS.UPDATE_STUDIO(id)
        : API_ENDPOINTS.SEAT_LAYOUTS.UPDATE_ADMIN(id),
      data,
      this.atStudio(studioId),
    );
  }

  async deleteSeatLayout(id: string, studioId?: string | null) {
    return this.delete(
      this.isStudio()
        ? API_ENDPOINTS.SEAT_LAYOUTS.DELETE_STUDIO(id)
        : API_ENDPOINTS.SEAT_LAYOUTS.DELETE_ADMIN(id),
      this.atStudio(studioId),
    );
  }

  async duplicateSeatLayout(id: string, studioId?: string | null) {
    return this.post(
      this.isStudio()
        ? API_ENDPOINTS.SEAT_LAYOUTS.DUPLICATE_STUDIO(id)
        : API_ENDPOINTS.SEAT_LAYOUTS.DUPLICATE_ADMIN(id),
      {},
      this.atStudio(studioId),
    );
  }

  /** Member-facing seat map — bare seat ids, never identities. */
  async getSessionSeatMap(sessionId: string, since?: number) {
    return this.get(API_ENDPOINTS.SESSION_SEATMAP(sessionId), {
      params: since !== undefined ? { since } : undefined,
    });
  }

  /** Staff seat map — includes who holds each machine. AdminOrStudio only. */
  async getSessionSeatMapStaff(sessionId: string) {
    return this.get(API_ENDPOINTS.SESSION_SEATMAP_STAFF(sessionId));
  }

  // ── Messaging ───────────────────────────────────────────────────────────
  // Studio roles are pinned by the JWT (plus the x-studio-id header, which the
  // caller picks under "All locations" — each location has its own messaging
  // setup); admins must pass studioId explicitly as a query param.
  private msgScope(studioId?: string) {
    return this.isStudio() || !studioId ? undefined : { studioId };
  }

  /** The x-studio-id pin for a studio role's messaging call. */
  private msgPin(studioId?: string): AxiosRequestConfig {
    return this.isStudio() && studioId ? this.atStudio(studioId) : {};
  }

  async getMessagingOverview(studioId?: string, range = '7d') {
    return this.get(API_ENDPOINTS.MESSAGING.OVERVIEW, {
      params: { range, ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  async getMessagingChannels(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.CHANNELS, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async updateMessagingChannel(data: any, studioId?: string) {
    return this.patch(API_ENDPOINTS.MESSAGING.CHANNELS, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** Write-only — the backend never returns these values. */
  async setMessagingCredentials(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.CREDENTIALS, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** Unlinks the studio's own Twilio account and switches WhatsApp off. */
  async disconnectMessagingChannel(channel: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.DISCONNECT, { channel }, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** Live check of the studio's Twilio account, sender and webhook URL. */
  async verifyMessagingChannel(channel = 'whatsapp', studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.VERIFY, {
      params: { channel, ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  /** Content templates that exist on the studio's own Twilio account. */
  async getProviderTemplates(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.PROVIDER_TEMPLATES, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async syncMessagingTemplates(studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.TEMPLATES_SYNC, {}, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async submitMessagingTemplate(templateKey: string, category: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.TEMPLATES_SUBMIT, { templateKey, category }, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async deleteMessagingTemplate(id: string, studioId?: string) {
    return this.delete(API_ENDPOINTS.MESSAGING.TEMPLATE_DELETE(id), {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMessagingTemplates(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.TEMPLATES, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** Create a WhatsApp template in Twilio from the panel, link it and (by default) submit it. */
  async createMessagingTemplate(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.TEMPLATES_CREATE, data, {
      params: this.msgScope(studioId),
    });
  }

  async upsertMessagingTemplate(data: any, studioId?: string) {
    return this.patch(API_ENDPOINTS.MESSAGING.TEMPLATES, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMessagingNotificationTypes(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.NOTIFICATION_TYPES, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMessages(params: any = {}, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.MESSAGES, {
      params: { ...params, ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  async getMessageDetail(id: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.MESSAGE_DETAIL(id), {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** "Why didn't my member get this?" */
  async lookupMemberMessages(userId: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.LOOKUP(userId), {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async messagingTestSend(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.TEST_SEND, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** Runs a notification through the real pipeline; lands in the Delivery Log. */
  async messagingTestPipeline(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.TEST_PIPELINE, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async searchMessagingMembers(q: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.MEMBERS, {
      params: { q, ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  // ── WhatsApp workflows (campaigns + automations) ──
  async getWorkflowMeta(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.WORKFLOWS_META, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async previewWorkflow(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.WORKFLOWS_PREVIEW, data, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async getWorkflows(kind?: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.WORKFLOWS, {
      params: { ...(kind ? { kind } : {}), ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  async getWorkflow(id: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.WORKFLOW(id), { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async createWorkflow(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.WORKFLOWS, data, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async updateWorkflow(id: string, data: any, studioId?: string) {
    return this.patch(API_ENDPOINTS.MESSAGING.WORKFLOW(id), data, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async workflowAction(id: string, action: 'cancel' | 'pause' | 'resume' | 'run-now', studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.WORKFLOW_ACTION(id, action), {}, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async deleteWorkflow(id: string, studioId?: string) {
    return this.delete(API_ENDPOINTS.MESSAGING.WORKFLOW(id), { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  /** Automatic WhatsApp messages (welcome, pack purchased, …), each with its own on/off. */
  async getAutomations(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.AUTOMATIONS, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  async setAutomation(key: string, enabled: boolean, studioId?: string) {
    return this.patch(API_ENDPOINTS.MESSAGING.AUTOMATION(key), { enabled }, { params: this.msgScope(studioId), ...this.msgPin(studioId) });
  }

  /** Member replies (inbound WhatsApp), newest first. */
  async getMessagingInbound(params: any = {}, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.INBOUND, {
      params: { ...params, ...this.msgScope(studioId) }, ...this.msgPin(studioId),
    });
  }

  /** Free-text reply — only inside the member's 24h window. */
  async replyToInbound(id: string, body: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.INBOUND_REPLY(id), { body }, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** How many members an "all members" send would reach. */
  async getMessagingMembersSummary(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.MEMBERS_SUMMARY, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  /** One member (`userId`), several (`userIds`) or every member (`allMembers`). */
  async sendMessageToMember(data: any, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.SEND_TO_MEMBER, data, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMemberConsent(userId: string, studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.CONSENT(userId), {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async optOutMember(userId: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.CONSENT_OPT_OUT(userId), {}, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async optInMember(userId: string, channel: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.CONSENT_OPT_IN(userId), { channel }, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMessagingSuppressions(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.SUPPRESSIONS, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async removeMessagingSuppression(address: string, channel: string, studioId?: string) {
    return this.post(API_ENDPOINTS.MESSAGING.SUPPRESSIONS_REMOVE, { address, channel }, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  async getMessagingHealth(studioId?: string) {
    return this.get(API_ENDPOINTS.MESSAGING.HEALTH, {
      params: this.msgScope(studioId), ...this.msgPin(studioId),
    });
  }

  // Super-admin ops
  async getMessagingProviders() {
    return this.get(API_ENDPOINTS.MESSAGING.ADMIN_PROVIDERS);
  }

  async getMessagingQueues() {
    return this.get(API_ENDPOINTS.MESSAGING.ADMIN_QUEUES);
  }

  async getMessagingTemplateMatrix() {
    return this.get(API_ENDPOINTS.MESSAGING.ADMIN_MATRIX);
  }

  async setMessagingTemplateStatus(id: string, status: string, rejectionReason?: string) {
    return this.patch(API_ENDPOINTS.MESSAGING.ADMIN_TEMPLATE_STATUS(id), {
      status,
      rejectionReason,
    });
  }

  async setMessagingKillSwitch(data: any) {
    return this.post(API_ENDPOINTS.MESSAGING.ADMIN_KILL_SWITCH, data);
  }

  async getMessagingCosts(range = '30d') {
    return this.get(API_ENDPOINTS.MESSAGING.ADMIN_COSTS, { params: { range } });
  }

  async getMessagingFailures(range = '7d') {
    return this.get(API_ENDPOINTS.MESSAGING.ADMIN_FAILURES, { params: { range } });
  }

  // Instructor management
  /**
   * @param studioId omit/empty for a super admin, who has no studio of their
   *   own — that used to interpolate an empty string and request
   *   `/admin/getAllInstructors/`, which 404s.
   */
  /**
   * The instructors LIST. Deliberately NOT pinned to `studioId`: under "All
   * locations" this page is meant to aggregate, and pinning it to the
   * caller's default branch would quietly defeat that. Use
   * `getInstructorsByStudio` when you mean one specific branch — which is
   * what every form dialog means.
   */
  async getInstructors(studioId?: string, params?: any) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.INSTRUCTORS.LIST_STUDIO
      : studioId
        ? API_ENDPOINTS.INSTRUCTORS.LIST(studioId)
        : API_ENDPOINTS.INSTRUCTORS.LIST_ALL;
    return this.get(endpoint, { params });
  }

  async getInstructorById(id: string) {
    return this.get(API_ENDPOINTS.INSTRUCTORS.DETAIL(id));
  }

  async createInstructor(studioId: string, data: any) {
    if (this.isStudio()) {
      // Pinned: under "All locations" the switcher sends __all__, which the
      // server refuses for writes.
      return this.post(API_ENDPOINTS.INSTRUCTORS.CREATE_STUDIO, data, this.atStudio(studioId));
    }
    return this.post(API_ENDPOINTS.INSTRUCTORS.CREATE(studioId), data);
  }

  async updateInstructor(id: string, data: any, studioId?: string | null) {
    if (this.isStudio()) {
      return this.patch(API_ENDPOINTS.INSTRUCTORS.UPDATE_STUDIO, { ...data, instructorId: id }, this.atStudio(studioId));
    }
    return this.patch(API_ENDPOINTS.INSTRUCTORS.UPDATE(id), data);
  }

  async deleteInstructor(
    id: string,
    replacementInstructorId?: string,
    notifyMembers?: boolean,
    studioId?: string | null,
  ) {
    // Both are query params — DELETE carries no body through this client.
    const params: Record<string, string> = {};
    if (replacementInstructorId) params.replacementInstructorId = replacementInstructorId;
    if (notifyMembers) params.notifyMembers = 'true';
    if (this.isStudio()) {
      return this.delete(API_ENDPOINTS.INSTRUCTORS.DELETE_STUDIO(id), { params, ...this.atStudio(studioId) });
    }
    return this.delete(API_ENDPOINTS.INSTRUCTORS.DELETE(id), { params });
  }

  /**
   * Disabling is a REMOVAL — the backend refuses it while the instructor still
   * has upcoming sessions or classes, unless `replacementInstructorId` names
   * who takes them over. The refusal carries the counts, so the caller can put
   * up the picker without asking again.
   */
  async setInstructorDisabled(
    id: string,
    isDisabled: boolean,
    replacementInstructorId?: string,
    notifyMembers?: boolean,
    studioId?: string | null,
  ) {
    // notifyMembers is the operator's decision, not a default: members who
    // already booked are only messaged when the studio asks for it.
    const body: any = { isDisabled, replacementInstructorId, notifyMembers: !!notifyMembers };
    if (this.isStudio()) {
      return this.patch(API_ENDPOINTS.INSTRUCTORS.SET_DISABLED_STUDIO(id), body, this.atStudio(studioId));
    }
    // Super admin: a studioId means "at this location" (a studio's own page);
    // none means every location they teach at.
    if (studioId) body.studioId = studioId;
    return this.patch(API_ENDPOINTS.INSTRUCTORS.SET_DISABLED(id), body);
  }

  /** What an instructor is still scheduled to teach. */
  async getInstructorFutureWork(id: string) {
    if (this.isStudio()) {
      return this.get(API_ENDPOINTS.INSTRUCTORS.FUTURE_WORK_STUDIO(id));
    }
    return this.get(API_ENDPOINTS.INSTRUCTORS.FUTURE_WORK(id));
  }

  /**
   * One instructor's performance — workload, demand and members, for a date
   * range. Same route for admin and studio: the guard scopes a studio token to
   * its own instructors, so there is no isStudio() branch here.
   */
  async getInstructorPerformance(
    id: string,
    params?: { startDate?: string; endDate?: string },
  ) {
    return this.get(API_ENDPOINTS.INSTRUCTORS.PERFORMANCE(id), { params });
  }

  /** Every instructor at the studio, one row each, for comparison. */
  async getInstructorPerformanceSummary(params?: {
    startDate?: string;
    endDate?: string;
    studioId?: string;
  }) {
    return this.get(API_ENDPOINTS.INSTRUCTORS.PERFORMANCE_SUMMARY, { params });
  }

  /**
   * Owner-only studio analytics. Server-side this is StudioAdminOnlyGuard, so
   * the studio is taken from the token — there is no studioId to pass and a
   * manager/instructor token is rejected outright.
   */
  async getStudioAnalytics(params: { startDate: string; endDate: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.STUDIO_ANALYTICS, { params });
  }

  // Drill-down for one stream slice (membership/classes/…) of the Analytics
  // "Total Revenue" breakdown popup — same classification studioAnalytics
  // itself uses, so the row count always matches what the popup showed.
  async getStudioAnalyticsSegment(params: {
    stream: string;
    // Narrows a stream to just its Razorpay ('online') or Cash/Manual
    // ('cash') rows — omit for streams that don't split (e.g. 'cancelled').
    mode?: 'online' | 'cash';
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) {
    return this.get(API_ENDPOINTS.STUDIOS.STUDIO_ANALYTICS_SEGMENT, { params });
  }

  // "Revenue by pack" card — grouped-by-title, sourced from the transaction
  // ledger (same classification studioAnalytics' Membership stream uses),
  // NOT the purchasedpacks collection — a deleted/free/redeemed pack can no
  // longer be trusted to answer "how much did this pack actually make".
  async getStudioPackRevenue(params: { startDate: string; endDate: string }) {
    return this.get(API_ENDPOINTS.STUDIOS.STUDIO_ANALYTICS_PACK_REVENUE, { params });
  }

  // Review management
  async getReviews(params?: any) {
    // Studio roles hit the scoped route; the server pins them to their own
    // studio regardless of what is sent.
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.REVIEWS.LIST_STUDIO
      : API_ENDPOINTS.REVIEWS.LIST;
    return this.get(endpoint, { params });
  }

  async getReviewById(id: string) {
    return this.get(API_ENDPOINTS.REVIEWS.DETAIL(id));
  }

  // Promo code management
  async getPromoCodes(params?: any) {
    return this.get(API_ENDPOINTS.PROMO_CODES.LIST, { params });
  }

  async getPromoCodeById(id: string) {
    return this.get(API_ENDPOINTS.PROMO_CODES.DETAIL(id));
  }

  async createPromoCode(data: any) {
    return this.post(API_ENDPOINTS.PROMO_CODES.CREATE, data);
  }

  async updatePromoCode(id: string, data: any) {
    return this.patch(API_ENDPOINTS.PROMO_CODES.UPDATE(id), { ...data, promoCodeId: id });
  }

  async deletePromoCode(id: string) {
    return this.delete(API_ENDPOINTS.PROMO_CODES.DELETE(id));
  }

  // Coupons
  async getCoupons(params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.COUPONS.LIST_STUDIO : API_ENDPOINTS.COUPONS.LIST;
    return this.get(endpoint, { params });
  }

  async getCouponById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.COUPONS.DETAIL_STUDIO(id) : API_ENDPOINTS.COUPONS.DETAIL(id);
    return this.get(endpoint);
  }

  async createCoupon(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.COUPONS.CREATE_STUDIO : API_ENDPOINTS.COUPONS.CREATE;
    return this.post(endpoint, data, this.atStudio(studioId));
  }

  async updateCoupon(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.COUPONS.UPDATE_STUDIO : API_ENDPOINTS.COUPONS.UPDATE;
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  async deleteCoupon(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.COUPONS.DELETE_STUDIO(id) : API_ENDPOINTS.COUPONS.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  // Packs methods
  async getPacks(params?: any, pinStudioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.LIST_STUDIO : API_ENDPOINTS.PACKS.LIST;
    return this.get(endpoint, { params, ...this.atStudio(pinStudioId) });
  }

  async getPackById(id: string) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.DETAIL_STUDIO(id) : API_ENDPOINTS.PACKS.DETAIL(id);
    return this.get(endpoint);
  }

  async createPack(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.CREATE_STUDIO : API_ENDPOINTS.PACKS.CREATE;
    return this.post(endpoint, data, this.atStudio(studioId));
  }

  async updatePack(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.UPDATE_STUDIO : API_ENDPOINTS.PACKS.UPDATE;
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  /** Members holding a still-valid pack from this template, by their own session type. */
  async getPackHolders(id: string, studioId?: string | null): Promise<{ activeMembers: number; bySessionType: Record<string, number> }> {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.HOLDERS_STUDIO(id) : API_ENDPOINTS.PACKS.HOLDERS(id);
    return this.get(endpoint, this.atStudio(studioId));
  }

  async deletePack(id: string, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.DELETE_STUDIO(id) : API_ENDPOINTS.PACKS.DELETE(id);
    return this.delete(endpoint, this.atStudio(studioId));
  }

  async togglePackStatus(id: string, isActive: boolean, studioId?: string | null) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.TOGGLE_STATUS_STUDIO(id) : API_ENDPOINTS.PACKS.TOGGLE_STATUS(id);
    return this.patch(endpoint, { isActive }, this.atStudio(studioId));
  }

  async getPurchasedPacks(params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.PURCHASED_LIST_STUDIO : API_ENDPOINTS.PACKS.PURCHASED_LIST;
    return this.get(endpoint, { params });
  }

  async getPurchasedPackById(id: string) {
    return this.get(API_ENDPOINTS.PACKS.PURCHASED_DETAIL(id));
  }

  async getPurchasedPacksStats() {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.PURCHASED_STATS_STUDIO : API_ENDPOINTS.PACKS.PURCHASED_STATS;
    return this.get(endpoint);
  }

  async getPurchasedPackHistory(id: string) {
    return this.get(API_ENDPOINTS.PACKS.PURCHASED_HISTORY(id));
  }

  async getUserBookingHistory(userId: string, params?: any) {
    // Studio owners get this user's bookings AT their studio only.
    const endpoint = this.isStudio() ? API_ENDPOINTS.BOOKINGS.LIST_STUDIO : API_ENDPOINTS.BOOKINGS.LIST;
    return this.get(endpoint, { params: { ...params, userId } });
  }

  async getUserRemoveWarnings(userId: string) {
    try {
      const bookingsRes = await this.getUserBookingHistory(userId, {
        page: 1,
        pageSize: 1,
        isUpcoming: true,
      });
      const hasUpcomingBookings = (bookingsRes?.count ?? 0) > 0;

      let hasActivePacks = false;
      if (this.isStudio()) {
        const packsRes = await this.get('/api/v1/packs/admin/studio/user-active-packs', {
          params: { userId, includeUpcoming: 'true' },
        });
        const packData = packsRes?.data ?? packsRes;
        const activeCount = (packData?.packs ?? []).length;
        const upcomingCount = (packData?.upcomingPacks ?? []).length;
        hasActivePacks = activeCount + upcomingCount > 0;
      } else {
        const packsRes = await this.getUserPackHistory(userId, {
          page: 1,
          pageSize: 1,
          status: 'active',
        });
        hasActivePacks = (packsRes?.count ?? 0) > 0;
      }

      return { hasUpcomingBookings, hasActivePacks };
    } catch {
      return { hasUpcomingBookings: false, hasActivePacks: false };
    }
  }

  async getStudioScopedBookings(params?: any) {
    return this.get(API_ENDPOINTS.BOOKINGS.LIST_STUDIO, { params });
  }

  async getUserPackHistory(userId: string, params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.PACKS.PURCHASED_LIST_STUDIO : API_ENDPOINTS.PACKS.PURCHASED_LIST;
    return this.get(endpoint, { params: { ...params, userId } });
  }

  async getUserTransactionHistory(userId: string, params?: any) {
    const endpoint = this.isStudio() ? API_ENDPOINTS.TRANSACTIONS.LIST_STUDIO : API_ENDPOINTS.TRANSACTIONS.LIST;
    return this.get(endpoint, { params: { ...params, userId } });
  }

  async updatePurchasedPack(id: string, data: { classesLeft?: number; expiryDate?: string; packEndDate?: string; startDate?: string; isPaid?: true }, studioId?: string | null) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.PACKS.PURCHASED_EDIT_STUDIO(id)
      : API_ENDPOINTS.PACKS.PURCHASED_EDIT(id);
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  async deletePurchasedPack(id: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.PACKS.PURCHASED_DELETE(id), this.atStudio(studioId));
  }

  async freezePackAdmin(
    id: string,
    data: { startDate: string; endDate: string; reason?: string },
    studioId?: string | null,
  ) {
    // `endDate` here is the day the pack RESUMES, matching what the member sees
    // in the Purple Pilates app. The backend converts it to the stored
    // last-frozen-day form; without this flag it would read the date as the last
    // frozen day and pause the member for one extra day.
    return this.post(
      API_ENDPOINTS.PACKS.FREEZE(id),
      { ...data, endDateExclusive: true },
      this.atStudio(studioId),
    );
  }

  async unfreezePackAdmin(id: string, studioId?: string | null) {
    return this.post(API_ENDPOINTS.PACKS.UNFREEZE(id), undefined, this.atStudio(studioId));
  }

  async deleteFreezeHistoryEntry(packId: string, entryId: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.PACKS.FREEZE_HISTORY_DELETE(packId, entryId), this.atStudio(studioId));
  }

  async checkUserByPhoneForPack(phoneNumber: string, pinStudioId?: string | null) {
    return this.post(API_ENDPOINTS.PACKS.MANUAL_CHECK_USER, { phoneNumber }, this.atStudio(pinStudioId));
  }

  async createUserForPackAssign(data: { phoneNumber: string; name: string; email: string; gender?: string }, pinStudioId?: string | null) {
    return this.post(API_ENDPOINTS.PACKS.MANUAL_CREATE_USER, data, this.atStudio(pinStudioId));
  }

  async manualAssignPack(data: {
    userId: string;
    packId: string;
    isPaid: boolean;
    basePrice?: number;
    taxes?: number;
    modeOfPayment?: string;
    appSource?: string;
    startDate?: string;
    /** Assign past the pack's per-member purchase limit (staff confirmed). */
    overrideLimit?: boolean;
  }, studioId?: string | null) {
    // studioId: the pack's location — under "All locations" the switcher sends
    // __all__, which the server refuses for writes.
    return this.post(API_ENDPOINTS.PACKS.MANUAL_ASSIGN, data, this.atStudio(studioId));
  }

  async validateCoupon(data: any) {
    return this.post(API_ENDPOINTS.COUPONS.VALIDATE, data);
  }

  // File upload
  async uploadFile(file: File, uploadType: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadType', uploadType);

    return this.post(API_ENDPOINTS.COMMON.UPLOAD_FILE, formData);
  }

  // Multiple file upload (Admin only)
  async uploadFiles(files: File[], uploadType: string) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('uploadType', uploadType);

    // Let axios/browser set multipart boundary — a bare `multipart/form-data`
    // header without boundary corrupts the body and S3 rejects the upload.
    // AdminOrStudioGuard POST: pinned to the account's own studio under "All
    // locations", where `__all__` would be refused.
    return this.post(API_ENDPOINTS.COMMON.UPLOAD_FILES, formData, this.atOwnStudio());
  }

  // Advertisement management
  async getAdvertisements(params?: any) {
    return this.get(API_ENDPOINTS.ADVERTISEMENTS.LIST, { params });
  }

  async getAdvertisementById(id: string) {
    return this.get(API_ENDPOINTS.ADVERTISEMENTS.DETAIL(id));
  }

  async createAdvertisement(data: any) {
    return this.post(API_ENDPOINTS.ADVERTISEMENTS.CREATE, data);
  }

  async updateAdvertisement(data: any) {
    return this.patch(API_ENDPOINTS.ADVERTISEMENTS.UPDATE, data);
  }

  async toggleAdvertisementStatus(id: string, isLive: boolean) {
    return this.patch(API_ENDPOINTS.ADVERTISEMENTS.TOGGLE_STATUS(id), { isLive });
  }

  async deleteAdvertisement(id: string) {
    return this.delete(API_ENDPOINTS.ADVERTISEMENTS.DELETE(id));
  }

  // Studio-scoped advertisement management (StudioGuard — only the studio's own ads)
  async getStudioAdvertisements(params?: any) {
    return this.get(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_LIST, { params });
  }

  async getStudioAdvertisementById(id: string) {
    return this.get(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_DETAIL(id));
  }

  // studioId pins the write — under "All locations" the switcher sends
  // __all__, which the server refuses for writes.
  async createStudioAdvertisement(data: any, studioId?: string | null) {
    return this.post(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_CREATE, data, this.atStudio(studioId));
  }

  async updateStudioAdvertisement(data: any, studioId?: string | null) {
    return this.patch(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_UPDATE, data, this.atStudio(studioId));
  }

  async toggleStudioAdvertisementStatus(id: string, isLive: boolean, studioId?: string | null) {
    return this.patch(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_TOGGLE_STATUS(id), { isLive }, this.atStudio(studioId));
  }

  async deleteStudioAdvertisement(id: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.ADVERTISEMENTS.STUDIO_DELETE(id), this.atStudio(studioId));
  }

  // Notification management (legacy)
  async sendNotificationLegacy(data: any) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.SEND_LEGACY, data);
  }

  // Website Inquiries
  async getWebsiteInquiries(params?: any) {
    return this.get(API_ENDPOINTS.COMMON.WEBSITE_INQUIRIES, { params });
  }

  // Corporate Leads
  async getCorporateLeads(params?: any) {
    return this.get(API_ENDPOINTS.COMMON.CORPORATE_LEADS, { params });
  }

  async getStudioContactInquiries(params?: any) {
    return this.get(API_ENDPOINTS.COMMON.STUDIO_CONTACT_INQUIRIES, { params });
  }

  // Leads
  async getLeads(params?: any) {
    return this.get(API_ENDPOINTS.LEADS.LIST, { params });
  }

  // Waitlist
  async getWaitlistEntries(params?: any) {
    return this.get(API_ENDPOINTS.WAITLIST.LIST, { params });
  }

  async getWaitlistStats() {
    return this.get(API_ENDPOINTS.WAITLIST.STATS);
  }

  // Partner / Whitelabel
  async generatePartnerApiKey(studioId: string) {
    return this.post(API_ENDPOINTS.PARTNER.GENERATE_API_KEY(studioId));
  }

  async revokePartnerApiKey(studioId: string) {
    return this.post(API_ENDPOINTS.PARTNER.REVOKE_API_KEY(studioId));
  }

  async getPartnerStudios() {
    return this.get(API_ENDPOINTS.PARTNER.LIST_PARTNER_STUDIOS);
  }

  // Notifications / Campaigns
  async getNotificationCampaigns(page = 1, limit = 20) {
    return this.get(`${API_ENDPOINTS.NOTIFICATIONS.CAMPAIGNS}?page=${page}&limit=${limit}`);
  }

  async getNotificationCampaignById(id: string) {
    return this.get(API_ENDPOINTS.NOTIFICATIONS.CAMPAIGN_DETAIL(id));
  }

  async createNotificationCampaign(data: any) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.CAMPAIGNS, data, { timeout: CAMPAIGN_SEND_TIMEOUT_MS });
  }

  async deleteNotificationCampaign(id: string) {
    return this.delete(API_ENDPOINTS.NOTIFICATIONS.CAMPAIGN_DELETE(id));
  }

  async cancelScheduledCampaign(id: string) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.CAMPAIGN_CANCEL_SCHEDULE(id));
  }

  async getSegmentPreview(segment: string, segmentConfig: any = {}, includeMembers = false) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.SEGMENT_PREVIEW, {
      segment,
      segmentConfig,
      ...(includeMembers ? { includeMembers: true } : {}),
    });
  }

  async getNotificationTemplates() {
    return this.get(API_ENDPOINTS.NOTIFICATIONS.TEMPLATES);
  }

  async getNotificationLeads(daysBack = 30) {
    return this.get(`${API_ENDPOINTS.NOTIFICATIONS.LEADS}?daysBack=${daysBack}`);
  }

  async sendLeadEmailReminder(leadId: string) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.LEAD_EMAIL_REMINDER(leadId), {});
  }

  // Studio Notifications (scoped to studio's own users)
  async getStudioNotificationCampaigns(page = 1, limit = 20) {
    return this.get(`${API_ENDPOINTS.NOTIFICATIONS.STUDIO_CAMPAIGNS}?page=${page}&limit=${limit}`);
  }

  // studioId pins these to one location — under "All locations" the switcher
  // sends __all__, which the server refuses for writes (the preview is a POST).
  async createStudioNotificationCampaign(data: any, studioId?: string | null) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.STUDIO_CAMPAIGNS, data, { timeout: CAMPAIGN_SEND_TIMEOUT_MS, ...this.atStudio(studioId) });
  }

  async deleteStudioNotificationCampaign(id: string, studioId?: string | null) {
    return this.delete(API_ENDPOINTS.NOTIFICATIONS.STUDIO_CAMPAIGN_DELETE(id), this.atStudio(studioId));
  }

  async getStudioSegmentPreview(segment: string, segmentConfig: any = {}, includeMembers = false, studioId?: string | null) {
    return this.post(API_ENDPOINTS.NOTIFICATIONS.STUDIO_SEGMENT_PREVIEW, {
      segment,
      segmentConfig,
      ...(includeMembers ? { includeMembers: true } : {}),
    }, this.atStudio(studioId));
  }

  // Studio Users (Staff)
  async createStudioUser(data: any, studioId?: string | null) {
    const endpoint = this.isStudio() || this.isStudioUser()
      ? API_ENDPOINTS.STUDIO_USERS.STUDIO_CREATE
      : API_ENDPOINTS.STUDIO_USERS.ADMIN_CREATE;
    // Pinned to the person's HOME location when one was chosen — under "All"
    // the sentinel would otherwise refuse the write.
    return this.post(endpoint, data, this.atStudio(studioId));
  }

  async getStudioUsers(studioId?: string) {
    if (this.isStudio() || this.isStudioUser()) {
      return this.get(API_ENDPOINTS.STUDIO_USERS.STUDIO_LIST);
    }
    return this.get(API_ENDPOINTS.STUDIO_USERS.ADMIN_LIST(studioId!));
  }

  async updateStudioUser(id: string, data: any, studioId?: string | null) {
    const endpoint = this.isStudio() || this.isStudioUser()
      ? API_ENDPOINTS.STUDIO_USERS.STUDIO_UPDATE(id)
      : API_ENDPOINTS.STUDIO_USERS.ADMIN_UPDATE(id);
    return this.patch(endpoint, data, this.atStudio(studioId));
  }

  /**
   * Remove a staff member.
   *
   * When that person also teaches, this is a REMOVAL of an instructor and the
   * backend applies the same handover guard as the instructors endpoint — it
   * refuses with `has_future_assignments` until a replacement is named.
   */
  async deleteStudioUser(
    id: string,
    replacementInstructorId?: string,
    notifyMembers?: boolean,
    studioId?: string | null,
  ) {
    const endpoint = this.isStudio() || this.isStudioUser()
      ? API_ENDPOINTS.STUDIO_USERS.STUDIO_DELETE(id)
      : API_ENDPOINTS.STUDIO_USERS.ADMIN_DELETE(id);
    const params: Record<string, string> = {};
    if (replacementInstructorId) params.replacementInstructorId = replacementInstructorId;
    if (notifyMembers) params.notifyMembers = 'true';
    return this.delete(endpoint, { params, ...this.atStudio(studioId) });
  }

  async resetStudioUserPassword(data: { email: string; currentPassword: string; newPassword: string }) {
    return this.post(API_ENDPOINTS.STUDIO_USERS.RESET_PASSWORD, data);
  }

  // ── Booking Status Update ──────────────────────────────────────────────────
  async updateBookingStatus(bookingId: string, status: string, reason?: string, creditClassBack?: boolean, studioId?: string | null) {
    return this.patch(API_ENDPOINTS.BOOKINGS.UPDATE_STATUS(bookingId), { status, reason, creditClassBack }, this.atStudio(studioId));
  }

  // Mark an unpaid booking as paid (clears pending dues so the user can book again).
  async markBookingPaid(bookingId: string, studioId?: string | null) {
    return this.post('/api/v1/bookings/admin/markBookingPaid', { bookingId }, this.atStudio(studioId));
  }

  // ── Audit Logs ─────────────────────────────────────────────────────────────
  async getAuditLogs(params?: Record<string, any>) {
    const endpoint = this.isStudio()
      ? API_ENDPOINTS.AUDIT_LOGS.LIST_STUDIO
      : API_ENDPOINTS.AUDIT_LOGS.LIST;
    return this.get(endpoint, { params });
  }

  // ── Studio Payments ────────────────────────────────────────────────────────
  async getStudioPaymentDetails(id: string) {
    return this.get(API_ENDPOINTS.STUDIO_PAYMENTS.DETAILS(id));
  }

  // Studio-facing payouts (studio owner view + partner app)
  async getStudioPayouts(params?: Record<string, any>) {
    return this.get(API_ENDPOINTS.STUDIO_PAYOUTS.LIST, { params });
  }
  async getStudioPayoutSummary() {
    return this.get(API_ENDPOINTS.STUDIO_PAYOUTS.SUMMARY);
  }
  async getStudioPayoutDetails(id: string) {
    return this.get(API_ENDPOINTS.STUDIO_PAYOUTS.DETAILS(id));
  }
  // The detailed invoice PDF for a payout (studio owner). Returns a Blob.
  async getStudioPayoutReceipt(id: string): Promise<Blob> {
    return this.get(API_ENDPOINTS.STUDIO_PAYOUTS.RECEIPT(id), { responseType: 'blob' });
  }

  async generateStudioPaymentStatements(weekStart: string) {
    return this.post(API_ENDPOINTS.STUDIO_PAYMENTS.GENERATE, { weekStart });
  }

  async getStudioPaymentStatements(params?: Record<string, any>) {
    return this.get(API_ENDPOINTS.STUDIO_PAYMENTS.LIST, { params });
  }

  async getStudioPaymentsMRR() {
    return this.get(API_ENDPOINTS.STUDIO_PAYMENTS.MRR);
  }

  /**
   * Set (or clear) a manual settlement adjustment on a PENDING statement —
   * money owed to the studio that the automatic calculation cannot see, e.g. a
   * captured pack payment orphaned when its purchased pack was deleted.
   * Signed: positive pays more, negative claws back. Pass 0 to clear.
   */
  async setStudioPaymentAdjustment(
    id: string,
    amount: number,
    label?: string,
    note?: string,
  ) {
    return this.patch(API_ENDPOINTS.STUDIO_PAYMENTS.ADJUSTMENT(id), {
      amount,
      label,
      note,
    });
  }

  async markStudioPaymentPaid(
    id: string,
    transactionRef: string,
    notes?: string,
    discountAmount?: number,
    discountLabel?: string,
  ) {
    return this.patch(API_ENDPOINTS.STUDIO_PAYMENTS.MARK_PAID(id), {
      transactionRef,
      notes,
      discountAmount,
      discountLabel,
    });
  }

  async deleteStudioPaymentStatement(id: string) {
    return this.delete(API_ENDPOINTS.STUDIO_PAYMENTS.DELETE(id));
  }

  async sendStudioPaymentInvoice(id: string) {
    return this.post(API_ENDPOINTS.STUDIO_PAYMENTS.SEND_INVOICE(id), {});
  }

  // ── Referrals ──────────────────────────────────────────────────────────────
  // studioId is only ever sent by a super admin using the page's studio picker.
  // A studio owner's token already identifies their studio, and the backend
  // ignores the parameter for them — it will not let one studio read another's.

  // pinStudioId: a studio role's location, sent as x-studio-id. Under "All
  // locations" the switcher sends __all__, which the server refuses for writes
  // — and a programme belongs to one location, so its reads are pinned too.

  async getReferralProgram(studioId?: string, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.REFERRALS.PROGRAM, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  async upsertReferralProgram(payload: Record<string, any>, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.REFERRALS.PROGRAM, payload, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  /** Owner-only on the backend — a manager gets a 403 here by design. */
  async setReferralProgramActive(isActive: boolean, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.REFERRALS.PROGRAM_ACTIVE, { isActive }, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  async getReferralReport(params?: Record<string, any>, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.REFERRALS.REPORT, { params, ...this.atStudio(pinStudioId) });
  }

  async getReferralLedger(params?: Record<string, any>, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.REFERRALS.LEDGER, { params, ...this.atStudio(pinStudioId) });
  }

  async grantReferralBenefit(payload: Record<string, any>, pinStudioId?: string | null) {
    return this.post(API_ENDPOINTS.REFERRALS.GRANT, payload, this.atStudio(pinStudioId));
  }

  // studioId is the backend's tenancy check on this route, not a filter — a
  // super admin must name the studio whose reward they are revoking.
  async revokeReferralBenefit(id: string, reason: string, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.REFERRALS.REVOKE(id), { reason }, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  // ── Milestones ─────────────────────────────────────────────────────────────

  async getMilestoneProgram(studioId?: string, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.MILESTONES.PROGRAM, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  async upsertMilestoneProgram(payload: Record<string, any>, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.MILESTONES.PROGRAM, payload, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  /** Owner-only on the backend, like referrals. */
  async setMilestoneProgramActive(isActive: boolean, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.MILESTONES.PROGRAM_ACTIVE, { isActive }, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  async getMilestoneAwards(params?: Record<string, any>, pinStudioId?: string | null) {
    return this.get(API_ENDPOINTS.MILESTONES.AWARDS, { params, ...this.atStudio(pinStudioId) });
  }

  async fulfilMilestoneAward(id: string, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.MILESTONES.FULFIL(id), {}, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }

  async revokeMilestoneAward(id: string, reason: string, studioId?: string, pinStudioId?: string | null) {
    return this.patch(API_ENDPOINTS.MILESTONES.REVOKE(id), { reason }, {
      params: studioId ? { studioId } : undefined,
      ...this.atStudio(pinStudioId),
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
